import { api } from "@/lib/api/client";
import type { Modality } from "@/lib/api/courses";
import type { SessionStatus } from "@/lib/api/attendance";
import type { MeetingProvider } from "@/lib/api/tenant";

export type CourseUnitStatus = "draft" | "published";
/** Private to the teaching team; never published. `detail` is optional (up to 500 characters). */
export interface UnitActivity { id: string; label: string; minutes: number | null; detail?: string | null }

export interface CourseUnit {
  can_teach?: boolean;
  /** Current publication authority, separate from permission to save. */
  can_publish?: boolean;
  id: string;
  course_id: string;
  session_id: string | null;
  position: number;
  topic: string;
  learning_objectives: string[];
  preparation_guidance: string | null;
  recap: string | null;
  follow_up: string | null;
  /** Present only for the currently assigned teaching team. */
  activities?: UnitActivity[];
  teaching_notes?: string | null;
  status: CourseUnitStatus;
  revision: number;
  saved_revision: number;
  published_revision: number | null;
  publication: Pick<CourseUnit, "topic" | "learning_objectives" | "preparation_guidance" | "recap" | "follow_up"> | null;
  created_by: string;
  updated_by: string;
  published_by: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  session_date: string | null;
  session_starts_at: string | null;
  session_ends_at?: string | null;
  session_status: SessionStatus | null;
  session_modality: Modality | null;
  session_meeting_provider: MeetingProvider | null;
  /**
   * Removed in issue #321 Milestone 1 — the server no longer projects the
   * meeting URL onto a teaching unit. `session_meeting_url_overridden` remains,
   * because "this session differs from the course default" is a fact about the
   * plan; the URL itself is a destination, and destinations are answered in the
   * portal. Do not re-add: a field a card can read is a readiness decision
   * waiting to be re-implemented.
   */
  session_location: string | null;
  session_modality_overridden: boolean | null;
  session_meeting_provider_overridden: boolean | null;
  session_meeting_url_overridden: boolean | null;
  session_location_overridden: boolean | null;
}

export interface CreateCourseUnit {
  id: string;
  operation_id: string;
  position: number;
  topic: string;
  learning_objectives: string[];
  preparation_guidance: string | null;
  recap: string | null;
  follow_up: string | null;
  session_id: string | null;
  activities?: UnitActivity[];
  teaching_notes?: string | null;
}

export interface PatchCourseUnit {
  operation_id: string;
  revision: number;
  topic?: string;
  learning_objectives?: string[];
  preparation_guidance?: string | null;
  recap?: string | null;
  follow_up?: string | null;
  session_id?: string | null;
  activities?: UnitActivity[];
  teaching_notes?: string | null;
}

export type UnitOperationKind = "create" | "save" | "publish";
export type UnitOperation = {
  operation_id: string;
  kind: UnitOperationKind;
  course_id: string;
  unit_id: string;
} & ({ state: "committed"; unit: CourseUnit } | { state: "rejected" | "not_committed"; message: string; status?: number });

export interface PublishCourseUnit {
  operation_id: string;
  revision: number;
  saved_revision: number;
  published_revision: number | null;
}

export const courseUnitsApi = {
  list: (courseId: string) => api.get<CourseUnit[]>(`/courses/${courseId}/units`),
  create: (courseId: string, body: CreateCourseUnit) =>
    api.post<UnitOperation>(`/courses/${courseId}/units`, body),
  patch: (courseId: string, unitId: string, body: PatchCourseUnit) =>
    api.patch<UnitOperation>(`/courses/${courseId}/units/${unitId}`, body),
  publish: (courseId: string, unitId: string, body: PublishCourseUnit) =>
    api.post<UnitOperation>(`/courses/${courseId}/units/${unitId}/publish`, body),
  reconcile: (courseId: string, unitId: string, operationId: string, kind: UnitOperationKind) =>
    api.post<UnitOperation>(`/courses/${courseId}/units/${unitId}/operations/${operationId}/reconcile`, { kind }),
  delete: (courseId: string, unitId: string, revision: number) =>
    api.delete<void>(`/courses/${courseId}/units/${unitId}?revision=${revision}`),
};
